import { ReactNode } from 'react';

import { FieldProps, FieldRenderProps, useField } from 'react-final-form';

import {
  AutocompleteInput as Input,
  Option,
  Filter,
} from '@vk-hr-tek/ui/input';

import { compose, ValidateType } from '../validation';

/**
 * Свойства компонента {@link AutocompleteInput}
 */
interface AutocompleteInputProps<T extends object = {}> {
  /**
   * Метка для инпута.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Флаг, указывающий, используется ли поиск.
   */
  search?: boolean;

  /**
   * Уникальное имя поля формы.
   */
  name: string;

  /**
   * Дополнительная функция для обработки изменения значения.
   */
  onChange?: (value: string | undefined, item?: Option<T> | null) => void;

  /**
   * Текст плейсхолдера для инпута.
   */
  placeholder?: string;

  /**
   * Флаг, указывающий, является ли поле обязательным.
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Флаг, указывающий, можно ли очистить значение инпута.
   *
   * @defaultValue `false`
   */
  clearable?: boolean;

  /**
   * Флаг, указывающий, заблокировано ли поле для ввода.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Флаг, указывающий, идет ли загрузка данных.
   *
   * @defaultValue `false`
   */
  loading?: boolean;

  /**
   * Ограничение количества загружаемых опций.
   *
   * @defaultValue `50`
   */
  limit?: number;

  /**
   * Функция для загрузки опций по фильтру.
   */
  loadItems: (filter: Filter) => Promise<Option<T>[]>;

  /**
   * Функция для рендера опции.
   */
  renderOption?: (option: Option<T>, value?: string[] | undefined) => ReactNode;

  /**
   * Тултип для инпута.
   */
  tooltip?: ReactNode | null;

  /**
   * Флаг, указывающий, следует ли показывать ошибки.
   *
   * @defaultValue `true`
   */
  showError?: boolean;

  /**
   * Дополнительные фильтры для загрузки опций.
   */
  additionalFilters?: Record<string, string | number>;

  /**
   * Минимальная длина значения инпута для начала загрузки опций.
   */
  minInputValueLength?: number;

  /**
   * Атрибут для тестирования компонента.
   */
  testId?: string;

  /**
   * Атрибут для отмены дефолтного маржина в 16 пикселей у инпута.
   */
  withoutMargin?: boolean;
}

/**
 * Компонент автодополнения для формы с интеграцией в react-final-form.
 *
 * @public
 * @param label - Метка для инпута.
 * @param labelIcon - Иконка для метки.
 * @param search - Флаг, указывающий, используется ли поиск.
 * @param name - Уникальное имя поля формы.
 * @param onChange - Дополнительная функция для обработки изменения значения.
 * @param placeholder - Текст плейсхолдера для инпута.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param clearable - Флаг, указывающий, можно ли очистить значение инпута.
 * @param disabled - Флаг, указывающий, заблокировано ли поле для ввода.
 * @param loading - Флаг, указывающий, идет ли загрузка данных.
 * @param limit - Ограничение количества загружаемых опций.
 * @param loadItems - Функция для загрузки опций по фильтру.
 * @param renderOption - Функция для рендера опции.
 * @param tooltip - Тултип для инпута.
 * @param showError - Флаг, указывающий, следует ли показывать ошибки.
 * @param additionalFilters - Дополнительные фильтры для загрузки опций.
 * @param minInputValueLength - Минимальная длина значения инпута для начала загрузки опций.
 * @param testId - Атрибут для тестирования компонента.
 * @param withoutMargin - Атрибут для отмены дефолтного маржина в 16 пикселей у инпута.
 * @param defaultValue - Значение по умолчанию для поля формы.
 * @param validate - Валидаторы для поля формы.
 * @param validationWithoutTouch - Показывать ошибку без взаимодействия с полем.
 * @param validationWhenDisabled - Показывать ошибку если поле задизейблено.
 * @returns Элемент компонента автодополнения.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link AutocompleteInputProps}
 */
export const AutocompleteInput = <T extends object = {}>({
  label,
  labelIcon,
  search,
  name,
  onChange,
  placeholder,
  required = false,
  clearable = false,
  disabled = false,
  loading = false,
  limit = 50,
  loadItems,
  renderOption,
  tooltip,
  showError = true,
  additionalFilters,
  minInputValueLength,
  testId,
  withoutMargin = false,
  validate,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  ...rest
}: {
  defaultValue?: string | number;
  validate?: (ValidateType | undefined)[] | ValidateType;
  validationWithoutTouch?: boolean;
  validationWhenDisabled?: boolean;
} & AutocompleteInputProps<T> &
  FieldProps<string | undefined, FieldRenderProps<string | undefined>>) => {
  const { input, meta } = useField(name, {
    ...rest,
    validate: Array.isArray(validate) ? compose(validate) : validate,
  });

  const handleChange =
    (
      inputInner: typeof input,
      userOnChange:
        | ((value: string | undefined, item?: Option<T> | null) => void)
        | undefined,
    ) =>
    (value: string | undefined, option?: Option<T> | null) => {
      inputInner.onChange(value);
      userOnChange?.(value, option);
    };

  const errorDisabled = disabled || !validationWhenDisabled;

  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  return (
    <Input
      {...input}
      search={search}
      testId={testId}
      label={label}
      labelIcon={labelIcon}
      onChange={handleChange(input, onChange)}
      placeholder={placeholder}
      required={required}
      clearable={clearable}
      disabled={disabled}
      loading={loading}
      limit={limit}
      loadItems={loadItems}
      renderOption={renderOption}
      error={renderError}
      helperText={renderError && showError && meta.error}
      tooltip={tooltip}
      additionalFilters={additionalFilters}
      minInputValueLength={minInputValueLength}
      withoutMargin={withoutMargin}
    />
  );
};
